import { useCallback } from "react";
import { toNumber, toString } from 'lodash';

import { IProviderAddressInfoDtoAddressTypeEnum } from '@/dtos';
import { useSnackbar } from '@/modules/core/hooks';
import { State } from '@/modules/core/queries/useStates.query';
import { CreatedProviderDto, CreateProviderDto, UpdatedProviderDto, UpdateProviderDto } from '@/modules/UseMyMoney/queries';
import { useDocumentOcrStore } from '@/modules/UseMyMoney/stores';
import { ProviderForm, ProviderFormField } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks/useProviderForm';
import { useInvalidateOcrQuery } from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/hooks';

interface Props {
  onSetVisible: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  createProvider: (createProviderDto: CreateProviderDto) => Promise<CreatedProviderDto>;
  updateProvider: (createProviderDto: UpdateProviderDto) => Promise<UpdatedProviderDto>;
  handleChangeProvider: (providerId?: string) => void;
  editableProviderId?: string;
  states?: State[];
}

const useSubmitProviderModal = ({
  onSetVisible,
  setIsLoading,
  createProvider,
  updateProvider,
  handleChangeProvider,
  editableProviderId,
  states,
}: Props) => {
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { invalidateOcrQuery } = useInvalidateOcrQuery();
  const { pushProvider } = useDocumentOcrStore();

  const handleCreateProvider = useCallback(async (formValues: ProviderForm) => {
    try {
      setIsLoading(true);
      const addressState = states?.find((state) => state?.id === formValues[ProviderFormField.state]);
      const createdProvider = await createProvider({
        facility_name: formValues[ProviderFormField.providerName],
        addresses: [{
          line1: formValues[ProviderFormField.addressLine1],
          line2: formValues[ProviderFormField.addressLine2],
          city: formValues[ProviderFormField.city],
          address_type: IProviderAddressInfoDtoAddressTypeEnum.RESIDENTIAL,
          validated: false,
          state: {
            id: toNumber(addressState?.id),
            code: addressState?.code,
            name: addressState?.name,
          },
          zipcode: formValues[ProviderFormField.zipCode],
        }],
      });
      if (!createdProvider?.id) {
        return;
      }
      handleChangeProvider(toString(createdProvider?.id));
      setIsLoading(false);
      onSetVisible(false);
      handleAddPermanentSnackbar({
        text: `${createdProvider?.facility_name} added.`,
        closeIcon: true,
        autoRemove: true,
      });
      pushProvider({
        id: toString(createdProvider?.id),
        name: createdProvider?.facility_name,
        isSuggested: false,
        address: {
          city: createdProvider?.addresses?.[0]?.city,
          line1: createdProvider?.addresses?.[0]?.line1,
          line2: createdProvider?.addresses?.[0]?.line2 || undefined,
          zipcode: createdProvider?.addresses?.[0]?.zipcode,
          state: {
            id: toString(createdProvider?.addresses?.[0]?.state?.id),
            code: createdProvider?.addresses?.[0]?.state?.code,
            name: createdProvider?.addresses?.[0]?.state?.name,
          },
        },
      });
    } catch {
      setIsLoading(false);
    }
  }, [createProvider, onSetVisible, setIsLoading, handleAddPermanentSnackbar, handleChangeProvider, states, pushProvider]);

  const handleUpdateProvider = useCallback(async (formValues: ProviderForm) => {
    try {
      setIsLoading(true);
      const addressState = states?.find((state) => state?.id === formValues[ProviderFormField.state]);
      const updatedProvider = await updateProvider({
        providerId: editableProviderId as string,
        facility_name: formValues[ProviderFormField.providerName],
        addresses: [{
          line1: formValues[ProviderFormField.addressLine1],
          line2: formValues[ProviderFormField.addressLine2],
          city: formValues[ProviderFormField.city],
          address_type: IProviderAddressInfoDtoAddressTypeEnum.RESIDENTIAL,
          validated: false,
          state: {
            id: toNumber(addressState?.id),
            code: addressState?.code,
            name: addressState?.name,
          },
          zipcode: formValues[ProviderFormField.zipCode],
        }],
      });
      if (!updatedProvider?.id) {
        return;
      }
      setIsLoading(false);
      onSetVisible(false);
      handleAddPermanentSnackbar({
        text: `${updatedProvider?.facility_name} updated.`,
        closeIcon: true,
        autoRemove: true,
      });
      await invalidateOcrQuery();
    } catch {
      setIsLoading(false);
    }
  }, [updateProvider, onSetVisible, handleAddPermanentSnackbar, setIsLoading, invalidateOcrQuery, states, editableProviderId]);

  return {
    handleCreateProvider,
    handleUpdateProvider,
  };
};

export default useSubmitProviderModal;
